import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCalendarEvents, postCalendarEvent, deleteCalendarEvent } from '@/api/owner';
import { Plus, Trash2, Calendar as CalendarIcon, X, AlertCircle, CheckCircle2, MapPin, Clock } from 'lucide-react';

const arr = (s: any): any[] => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  if (Array.isArray(s.data)) return s.data;
  if (Array.isArray(s.data?.data)) return s.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const typeTone: Record<string, string> = {
  exam: 'bg-red-50 text-red-700 border-red-200',
  holiday: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  event: 'bg-blue-50 text-blue-700 border-blue-200',
  meeting: 'bg-amber-50 text-amber-700 border-amber-200',
  celebration: 'bg-pink-50 text-pink-700 border-pink-200',
  deadline: 'bg-purple-50 text-purple-700 border-purple-200',
};

/** Backend contract: null (everyone) or {roles:[...]} / {class_ids:[...]} */
function buildAudiencePayload(audience: string): any {
  if (audience === 'parents') return { roles: ['parent'] };
  if (audience === 'staff')   return { roles: ['teacher', 'desk', 'bursar', 'owner'] };
  if (audience === 'students')return { roles: ['student'] };
  return null; // 'all' → everyone
}

export function CalendarPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => { if (successMsg) { const t = setTimeout(() => setSuccessMsg(null), 4000); return () => clearTimeout(t); } }, [successMsg]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 6000); return () => clearTimeout(t); } }, [error]);

  const eventsQ = useQuery({ queryKey: ['owner-calendar'], queryFn: () => getCalendarEvents().then(unwrap) });
  const events = arr(eventsQ.data);

  const grouped = events.reduce((acc: any, e: any) => {
    if (!e.starts_at) return acc;
    const month = new Date(e.starts_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(e);
    return acc;
  }, {});

  const deleteM = useMutation({
    mutationFn: (id: number) => deleteCalendarEvent(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner-calendar'] }); setSuccessMsg('Event deleted.'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Delete failed'),
  });

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      {error && <div className="fixed top-4 right-4 z-[100] max-w-md bg-red-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3"><AlertCircle className="w-5 h-5 flex-shrink-0" /><p className="text-sm font-semibold flex-1">{error}</p><button onClick={() => setError(null)}><X className="w-4 h-4" /></button></div>}
      {successMsg && <div className="fixed top-4 right-4 z-[100] max-w-md bg-green-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3"><CheckCircle2 className="w-5 h-5 flex-shrink-0" /><p className="text-sm font-semibold flex-1">{successMsg}</p><button onClick={() => setSuccessMsg(null)}><X className="w-4 h-4" /></button></div>}

      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">COMMUNICATION</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Calendar</h1>
          <p className="text-slate-500 text-sm mt-1">School events, exams, holidays and meetings</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-5 py-2.5 text-sm font-bold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2 shadow-sm"><Plus className="w-4 h-4" /> New event</button>
      </div>

      {eventsQ.isLoading ? (
        <p className="text-sm text-slate-500 text-center py-8">Loading events…</p>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
          <CalendarIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No events yet</p>
          <p className="text-sm text-slate-500 mt-1">Add exams, holidays, meetings and other events.</p>
          <button onClick={() => setShowCreate(true)} className="mt-4 px-4 py-2 text-sm font-bold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add first event</button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(grouped).map(month => (
            <div key={month}>
              <h2 className="text-lg font-bold text-slate-900 mb-3">{month}</h2>
              <div className="space-y-2">
                {grouped[month].map((e: any) => (
                  <div key={e.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition flex justify-between gap-3">
                    <div className="flex gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-16 text-center bg-slate-50 rounded-lg p-2 border border-slate-200">
                        <p className="text-2xl font-extrabold text-slate-900">{new Date(e.starts_at).getDate()}</p>
                        <p className="text-xs font-bold text-slate-500 uppercase">{new Date(e.starts_at).toLocaleDateString('en-GB', { month: 'short' })}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-slate-900">{e.title}</h3>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${typeTone[e.type ?? 'event'] ?? 'bg-slate-100'}`}>{e.type ?? 'event'}</span>
                        </div>
                        {e.description && <p className="text-sm text-slate-600 mb-1">{e.description}</p>}
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {fmtDate(e.starts_at)}{e.ends_at && ` – ${fmtDate(e.ends_at)}`}</span>
                          {!e.all_day && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(e.starts_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
                          {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</span>}
                        </div>
                      </div>
                    </div>
                    <button onClick={async () => { if (await askConfirm('Delete this event?')) deleteM.mutate(e.id); }} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition self-start"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateEventModal onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['owner-calendar'] }); setSuccessMsg('Event added.'); }} onError={setError} />}
    </div>
  );
}

function CreateEventModal({ onClose, onSuccess, onError }: { onClose: () => void; onSuccess: () => void; onError: (m: string) => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('event');
  const [startsAt, setStartsAt] = useState(new Date().toISOString().slice(0, 16));
  const [endsAt, setEndsAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [notify, setNotify] = useState(false);
  const [audience, setAudience] = useState('all');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return onError('Title required');
    if (!startsAt) return onError('Start date required');
    setSaving(true);
    try {
      const audiencePayload: any = buildAudiencePayload(audience);
      

      await postCalendarEvent({
        title: title.trim(),
        description: description.trim() || null,
        type,
        starts_at: startsAt,
        ends_at: endsAt || null,
        all_day: allDay,
        location: location.trim() || null,
        notify,
        audience: audiencePayload,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">New event</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-4">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. PTA meeting" /></div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                <select value={type} onChange={(e) => setType(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
                  <option value="event">Event</option><option value="exam">Exam</option><option value="holiday">Holiday</option>
                  <option value="meeting">Meeting</option><option value="celebration">Celebration</option><option value="deadline">Deadline</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Audience</label>
                <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
                  <option value="all">Everyone</option><option value="parents">Parents</option><option value="staff">Staff</option><option value="students">Students</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Starts *</label><input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Ends</label><input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Location</label><input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. School hall" /></div>
              <div className="flex items-center gap-4 pt-6">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="w-4 h-4 text-blue-600 rounded" /><span className="text-sm font-semibold text-slate-700">All day</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4 text-emerald-600 rounded" /><span className="text-sm font-semibold text-slate-700">Notify</span></label>
              </div>
            </div>

            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Description</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            
            <button onClick={submit} disabled={saving} className="w-full py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving…' : 'Add event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
